/**
 * The survey editor's document: what a form looks like while it is being edited, and every rule
 * about what a trainer may do to it.
 *
 * ── Inclusion is a CHECKBOX, and that follows from the wire ──────────────────────────────────
 * `PATCH /survey/template/{id}` replaces the whole ordered item list in one write — there is no
 * add-question call and no remove-question call. A switch that wrote on press would therefore have
 * to resubmit the entire form on every tick, which turns a stray click into a save of whatever else
 * was half-typed. So inclusion is staged in this draft and committed by „Zapisz zmiany", and the
 * body `toPatchBody` builds is always the COMPLETE list rather than a delta. The canon states the
 * same decision in prose; this module is where it is true.
 *
 * ── One ordered list, projected into two sections ────────────────────────────────────────────
 * `items` holds every standard question the catalog offers — included or not, so an unticked one
 * keeps its place and its „required" — followed by the trainer's own. The wire order is the same:
 * the included standard questions, then the custom ones. Interleaving is not offered because the
 * canon draws two sections and nothing in the product asks a trainer to slot their own question
 * between two of ours; a reorder inside the custom run is the whole of R4's reordering here.
 *
 * ── What this module refuses, and why it refuses it rather than the server ───────────────────
 * Every rule below is one `SurveyTemplateProcessor` enforces too. It is asked here first because a
 * refusal that arrives after a round trip has already thrown away where the trainer was:
 *
 * · **R2** — the intake's four locked questions cannot be unticked, and `includeStandard` ignores
 *   the attempt rather than trusting a disabled checkbox to be the only way in.
 * · **R5** — a standard question appears at most once; hydration keys them by code.
 * · **R20** — a check-in is never offered the locked demographic questions, even if a catalog read
 *   were to hand them over.
 * · **R21** — the cadence's own three rules live in `cadenceForm`.
 * · **The clip cap** — at most three `ExerciseClip` questions per survey, and a clip question is
 *   ALWAYS optional: `AnswerType.ExerciseClip` is skippable by construction on the backend
 *   (`Required = authored.AnswerType != ExerciseClip && input.Required`), so offering the toggle
 *   would be offering a setting the server discards.
 *
 * ── Ids arrive, they are not minted ──────────────────────────────────────────────────────────
 * `addCustom` takes the id of the question it is adding. A reducer that called `crypto.randomUUID()`
 * would be a reducer no test could pin, and the caller has a perfectly good one.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import type {
  TrainWithMeModulesSurveysDtosRequestsCreateSurveyTemplateRequest as CreateSurveyTemplateRequest,
  TrainWithMeModulesSurveysDtosRequestsSurveyTemplateItemInput as SurveyTemplateItemInput,
  TrainWithMeModulesSurveysDtosRequestsUpdateSurveyTemplateRequest as UpdateSurveyTemplateRequest,
  TrainWithMeModulesSurveysDtosResponsesSurveyCatalogQuestion as SurveyCatalogQuestion,
  TrainWithMeModulesSurveysDtosResponsesSurveyCatalogResponse as SurveyCatalogResponse,
  TrainWithMeModulesSurveysDtosResponsesSurveyTemplateResponse as SurveyTemplateResponse,
  TrainWithMeModulesSurveysDtosSurveyQuestionView as SurveyQuestionView,
} from '~/shared/api/generated/model';

import { questionNames } from '~/shared/lib/standardQuestionNames';

import { EMPTY_CADENCE, cadenceProblems, toCadenceInput, type CadenceDraft } from './cadenceForm';
import type { ShelfQuestion } from './questionCatalog';
import type { TranslateKey } from './translateKey';

/**
 * The eight answer types a trainer may author, in the order the canon's 4 × 2 grid draws them.
 *
 * Three of the eleven `AnswerType` values are missing and each is missing for the same reason: the
 * goal builder, the full-body measurement form and the progress-photo capture are each the single
 * locked-in-code question of their kind, and `CustomQuestionRules.Validate` refuses a custom
 * question of any of them outright.
 */
export const AUTHORABLE_ANSWER_TYPES = [
  'ShortText',
  'LongText',
  'SingleChoice',
  'MultiChoice',
  'Number',
  'Scale',
  'Date',
  'ExerciseClip',
] as const;

export type AuthorableAnswerType = (typeof AUTHORABLE_ANSWER_TYPES)[number];

/** The two types that need a list to choose from. */
export const CHOICE_ANSWER_TYPES: readonly AuthorableAnswerType[] = ['SingleChoice', 'MultiChoice'];

/** `SurveyTemplateProcessor.MaxExerciseClipQuestions`, asked before the save rather than after it. */
export const MAX_EXERCISE_CLIPS = 3;

/** `CustomQuestionRules.NormalizeOptions` refuses fewer than two; the canon draws at most six rows. */
export const MIN_CHOICE_OPTIONS = 2;
export const MAX_CHOICE_OPTIONS = 6;

/**
 * `leadForm` is the public form's own template (public-form R4): the intake's catalog, edited in the
 * public form builder and in Raporty, with no lock, no cadence, no title of its own and three answer
 * types refused.
 */
export type SurveyKindKey = 'intake' | 'checkIn' | 'leadForm';

/**
 * Public-form R5: the goal builder, the exercise clip and photos cannot stand in a lead form, and the
 * backend refuses to save a template holding one. A person with no account has no goal model to
 * build into, no movement library to film against and no gallery to keep a photo in.
 */
export const LEAD_FORM_REFUSED_ANSWER_TYPES: readonly string[] = ['GoalBuilder', 'ExerciseClip', 'Photo'];

/** Whether a question of this answer type may stand in a survey of this kind. */
export function answerTypeAllowed(kind: SurveyKindKey, answerType: string | null | undefined): boolean {
  if (kind !== 'leadForm') return true;
  return !LEAD_FORM_REFUSED_ANSWER_TYPES.includes(answerType ?? '');
}

/** Whether the recording type is offered at all. A lead form refuses it (R5). */
export function allowsClips(draft: EditorDraft): boolean {
  return answerTypeAllowed(draft.kind, 'ExerciseClip');
}

/** One question from the system catalog, with the trainer's two decisions about it. */
export interface StandardDraftItem {
  readonly kind: 'standard';
  readonly code: string;
  readonly label: string;
  readonly answerType: string | null;
  readonly mapTarget: string | null;
  /** Which of the client's readings the answer is (`SleepDuration`, `EnergyLevel`), where it is one. */
  readonly vitalType: string | null;
  readonly section: string | null;
  /** R2: present and enabled in every intake, and never unticked. */
  readonly locked: boolean;
  /** Health data and the progress photo: the server discards a „required" on these. */
  readonly alwaysOptional: boolean;
  readonly included: boolean;
  readonly required: boolean;
}

/** One question the trainer wrote, or lifted off their shelf. */
export interface CustomDraftItem {
  readonly kind: 'custom';
  /** The template item's own id. Stable across a save, which is what a reorder has to preserve. */
  readonly id: string;
  readonly label: string;
  readonly answerType: AuthorableAnswerType;
  readonly options: readonly string[];
  readonly required: boolean;
  /** The movement an `ExerciseClip` question asks about. `null` for every other type. */
  readonly exerciseId: number | null;
  /** The shelf entry this was copied from, or `null`. Also what the bookmark fills in. */
  readonly sourceQuestionId: number | null;
}

export type DraftItem = StandardDraftItem | CustomDraftItem;

export interface EditorDraft {
  readonly kind: SurveyKindKey;
  /** `null` for a form that has not been created yet — `/raporty/nowa`. */
  readonly templateId: number | null;
  readonly title: string;
  readonly active: boolean;
  /** `null` for the intake, which is filled once rather than repeated (R6). */
  readonly cadence: CadenceDraft | null;
  readonly items: readonly DraftItem[];
  readonly dirty: boolean;
}

/** What a draft is before anything has been read. Renders as the editor's pending state. */
export const EMPTY_DRAFT: EditorDraft = {
  kind: 'checkIn',
  templateId: null,
  title: '',
  active: true,
  cadence: null,
  items: [],
  dirty: false,
};

/** One key space for both item kinds, so a single `setRequired` serves the whole form. */
export function itemKey(item: DraftItem): string {
  return item.kind === 'standard' ? `std:${item.code}` : `cst:${item.id}`;
}

/**
 * What one row of the editor is CALLED.
 *
 * The draft holds the WIRE's label, which for a catalog question is `StandardQuestion.DefaultLabel`
 * — English by design, and expected to be replaced by `intakeSurvey.qLabel.<code>` on every client.
 * The naming is done here rather than in `hydrateDraft` so a draft never carries a language: the
 * form survives a change of interface language, and `adopt`'s reference comparison keeps comparing
 * what was submitted rather than what was submitted in the language it was submitted in.
 *
 * A question the trainer wrote themselves has no code and keeps their own wording.
 */
export function questionName(item: DraftItem, t: TranslateKey): string {
  return item.kind === 'custom'
    ? item.label
    : questionNames(t, { code: item.code, label: item.label }).shortLabel;
}

export function standardItems(draft: EditorDraft): readonly StandardDraftItem[] {
  return draft.items.filter((item): item is StandardDraftItem => item.kind === 'standard');
}

export function customItems(draft: EditorDraft): readonly CustomDraftItem[] {
  return draft.items.filter((item): item is CustomDraftItem => item.kind === 'custom');
}

export function clipCount(draft: EditorDraft): number {
  return customItems(draft).filter((item) => item.answerType === 'ExerciseClip').length;
}

/** Whether a fourth recording would fit. The UI states the cap; this decides it. */
export function canAddClip(draft: EditorDraft): boolean {
  return clipCount(draft) < MAX_EXERCISE_CLIPS;
}

/** How many recordings are still on offer — the canon's „zostały 2 z 3". */
export function clipsLeft(draft: EditorDraft): number {
  return Math.max(0, MAX_EXERCISE_CLIPS - clipCount(draft));
}

// ── Hydration ────────────────────────────────────────────────────────────────────────────────

function catalogQuestions(
  catalog: SurveyCatalogResponse | null | undefined,
  kind: SurveyKindKey,
): readonly SurveyCatalogQuestion[] {
  const questions = (catalog?.questions ?? []).filter(
    (question) => typeof question.code === 'string' && question.code !== '',
  );
  // R20. `GET /survey/catalog?kind=CheckIn` already leaves them out; this is the rule stated on our
  // side, so a catalog that ever stopped filtering could not put a date of birth on a weekly form.
  if (kind === 'checkIn') return questions.filter((question) => question.isLocked !== true);
  // Public-form R5, stated here for the same reason: a catalog that offered the goal builder to a
  // lead form would let the trainer tick a question the save is then refused for.
  if (kind === 'leadForm') return questions.filter((question) => answerTypeAllowed(kind, question.answerType));
  return questions;
}

function isAuthorable(value: string | null | undefined): value is AuthorableAnswerType {
  return (AUTHORABLE_ANSWER_TYPES as readonly string[]).includes(value ?? '');
}

/**
 * A template's stored form, plus the catalog it was composed from, as one editable draft.
 *
 * The catalog decides which standard questions EXIST and in what order; the template decides which
 * of them are on and what each one's „required" is. A template item whose code is not in the
 * catalog is dropped — catalog drift, which `SurveyQuestionResolver.Resolve` skips at the other end
 * for the same reason.
 */
/**
 * What hydration reads off a stored form. A `SurveyTemplateResponse` is one; so is the public form's
 * `{ items }`, whose items are the template's stored config rather than the resolved view — the
 * fields read below are the ones both carry.
 */
export interface HydratableTemplate {
  readonly id?: number;
  readonly title?: string | null;
  readonly isActive?: boolean;
  readonly cadence?: SurveyTemplateResponse['cadence'];
  readonly items?: readonly SurveyTemplateItemInput[] | null;
}

export function hydrateDraft(input: {
  readonly kind: SurveyKindKey;
  readonly template: HydratableTemplate | null | undefined;
  readonly catalog: SurveyCatalogResponse | null | undefined;
}): EditorDraft {
  const { kind, template, catalog } = input;

  const stored = new Map<string, { required: boolean }>();
  const customs: CustomDraftItem[] = [];

  for (const item of template?.items ?? []) {
    if (typeof item.code === 'string' && item.code !== '') {
      // R5: the first occurrence wins and a second is not a second row.
      if (!stored.has(item.code)) stored.set(item.code, { required: item.required === true });
      continue;
    }
    if (typeof item.id !== 'string' || item.id === '') continue;
    // A refused type the wire still carried is not shown as something the trainer could keep: the
    // next save would be refused for it. Dropping it here makes the save the repair.
    if (!answerTypeAllowed(kind, item.answerType)) continue;
    customs.push({
      kind: 'custom',
      id: item.id,
      label: item.label ?? '',
      answerType: isAuthorable(item.answerType) ? item.answerType : 'ShortText',
      options: item.options ?? [],
      required: item.required === true,
      exerciseId: item.exerciseId ?? null,
      sourceQuestionId: item.sourceQuestionId ?? null,
    });
  }

  const standards: StandardDraftItem[] = catalogQuestions(catalog, kind).map((question) => {
    const code = question.code ?? '';
    const held = stored.get(code);
    // Public-form R4: locking is an intake rule. A lead form may drop any question.
    const locked = kind !== 'leadForm' && question.isLocked === true;
    // Health data and the progress photo: `StandardQuestion.IsAlwaysOptional`. The catalog does not
    // publish that flag, so it is read off the two facts that compose it, exactly as the C# does.
    const alwaysOptional = question.answerType === 'Photo' || question.section === 'health';
    return {
      kind: 'standard',
      code,
      label: question.label ?? '',
      answerType: question.answerType ?? null,
      mapTarget: question.mapTarget ?? null,
      vitalType: question.vitalType ?? null,
      section: question.section ?? null,
      locked,
      alwaysOptional,
      // The TEMPLATE decides, never `defaultEnabled`: a trainer who has never customised their
      // intake is still served the resolved default form by `GET /survey/template`, so the items
      // that came back already are the answer and the catalog's default would be a second one.
      included: locked || held !== undefined,
      required: alwaysOptional ? false : locked || (held?.required ?? question.defaultRequired === true),
    };
  });

  const cadence: CadenceDraft | null =
    kind !== 'checkIn'
      ? null
      : {
          frequency:
            template?.cadence?.frequency === 'Day' || template?.cadence?.frequency === 'Month'
              ? template.cadence.frequency
              : 'Week',
          dayOfWeek: template?.cadence?.dayOfWeek ?? null,
          dayOfMonth: template?.cadence?.dayOfMonth ?? null,
          // The wire carries an instant; the field takes a civil date. A cadence date has no zone
          // attached (R28), so the first ten characters are the whole of it.
          startDate: template?.cadence?.startDate?.slice(0, 10) ?? null,
        };

  return {
    kind,
    templateId: template?.id ?? null,
    title: template?.title ?? '',
    active: template?.isActive !== false,
    cadence,
    items: [...standards, ...customs],
    dirty: false,
  };
}

/** A check-in that does not exist yet: every catalog question off, and a rhythm with no date. */
export function newCheckInDraft(catalog: SurveyCatalogResponse | null | undefined): EditorDraft {
  const standards: StandardDraftItem[] = catalogQuestions(catalog, 'checkIn').map((question) => ({
    kind: 'standard',
    code: question.code ?? '',
    label: question.label ?? '',
    answerType: question.answerType ?? null,
    mapTarget: question.mapTarget ?? null,
    vitalType: question.vitalType ?? null,
    section: question.section ?? null,
    locked: false,
    alwaysOptional: question.answerType === 'Photo' || question.section === 'health',
    included: question.defaultEnabled === true,
    required: question.defaultRequired === true && question.answerType !== 'Photo',
  }));

  return { ...EMPTY_DRAFT, cadence: EMPTY_CADENCE, items: standards };
}

// ── The reducer ──────────────────────────────────────────────────────────────────────────────

export type EditorAction =
  | { readonly type: 'hydrate'; readonly draft: EditorDraft }
  /**
   * A FULL save has landed: take the server's answer, unless the trainer kept typing while it was
   * open. See the case below — `sent` is the very object that was submitted.
   */
  | { readonly type: 'adopt'; readonly sent: EditorDraft; readonly saved: EditorDraft }
  /** A NARROW write has landed — „Wstrzymaj ankietę". It changes one field and nothing else. */
  | { readonly type: 'setActive'; readonly active: boolean }
  | { readonly type: 'setTitle'; readonly title: string }
  | { readonly type: 'setCadence'; readonly cadence: Partial<CadenceDraft> }
  | { readonly type: 'includeStandard'; readonly code: string; readonly included: boolean }
  | { readonly type: 'setRequired'; readonly key: string; readonly required: boolean }
  | { readonly type: 'addCustom'; readonly id: string; readonly question: AuthoredQuestion }
  | { readonly type: 'updateCustom'; readonly id: string; readonly patch: Partial<CustomDraftItem> }
  | { readonly type: 'removeCustom'; readonly id: string }
  | { readonly type: 'move'; readonly id: string; readonly targetIndex: number };

/** What the „Nowe pytanie" card hands over. The id is the caller's, for the reason in the header. */
export interface AuthoredQuestion {
  readonly label: string;
  readonly answerType: AuthorableAnswerType;
  readonly options: readonly string[];
  readonly required: boolean;
  readonly exerciseId: number | null;
  readonly sourceQuestionId: number | null;
}

/**
 * The shelf entries this form may take. A lead form refuses three answer types (R5) and the reducer
 * drops a refused `addCustom` — so an entry it would drop is not offered at all, rather than offered,
 * picked, announced as added and silently missing.
 */
export function offeredShelf(draft: EditorDraft, shelf: readonly ShelfQuestion[]): readonly ShelfQuestion[] {
  return shelf.filter((question) => answerTypeAllowed(draft.kind, question.answerType));
}

/** A shelf entry as a question this survey can hold — a COPY, plus where it came from. */
export function shelfInsert(question: ShelfQuestion): AuthoredQuestion {
  return {
    label: question.label,
    answerType: isAuthorable(question.answerType) ? question.answerType : 'ShortText',
    options: question.options,
    required: question.required,
    // A shelf entry can never be a recording — `CustomQuestionRules` refuses to save one, because
    // the type names one specific movement — so there is no exercise to carry across.
    exerciseId: null,
    sourceQuestionId: question.id,
  };
}

function dirty(draft: EditorDraft, items: readonly DraftItem[]): EditorDraft {
  return { ...draft, items, dirty: true };
}

export function editorDraftReducer(draft: EditorDraft, action: EditorAction): EditorDraft {
  switch (action.type) {
    case 'hydrate':
      return action.draft;

    /**
     * The answer to a full save.
     *
     * The comparison is by REFERENCE, and that is exact rather than approximate: every case in this
     * reducer that changes anything returns a NEW object, so `draft === action.sent` means precisely
     * „nothing has been dispatched since this save was submitted". When that holds, the server's
     * answer is the whole truth and the draft becomes clean — which is also how the ids and the
     * normalisations the backend applied come back in.
     *
     * When it does NOT hold, keystrokes landed while the request was open. They stay, the draft
     * stays dirty, and only the identity the server minted is taken across — an earlier revision
     * hydrated over them and the trainer watched their last few edits disappear with no dialog.
     */
    case 'adopt':
      if (draft === action.sent) return action.saved;
      return { ...draft, templateId: action.saved.templateId ?? draft.templateId };

    /**
     * „Wstrzymaj ankietę" / „Wznów ankietę", once the PATCH has answered.
     *
     * It writes ONE field. The response carries the STORED template — the one without whatever is
     * currently being edited — so hydrating from it here is how an unsaved form is silently thrown
     * away, and `dirty` is deliberately left exactly as it was.
     */
    case 'setActive':
      return { ...draft, active: action.active };

    case 'setTitle':
      // R6/the intake's own refusal: `PATCH /survey/template` carries no title field at all, so a
      // rename here could not be saved and is not accepted. A lead form's PATCH takes items only.
      if (draft.kind !== 'checkIn') return draft;
      return { ...draft, title: action.title, dirty: true };

    case 'setCadence': {
      if (draft.cadence === null) return draft;
      return { ...draft, cadence: { ...draft.cadence, ...action.cadence }, dirty: true };
    }

    case 'includeStandard': {
      const items = draft.items.map((item) => {
        if (item.kind !== 'standard' || item.code !== action.code) return item;
        // R2. The checkbox is refused in the UI too; this is the rule rather than the drawing.
        if (item.locked) return item;
        return { ...item, included: action.included };
      });
      return dirty(draft, items);
    }

    case 'setRequired': {
      const items = draft.items.map((item) => {
        if (itemKey(item) !== action.key) return item;
        if (item.kind === 'standard') {
          if (item.locked || item.alwaysOptional) return item;
          return { ...item, required: action.required };
        }
        // A recording is always skippable — the server discards the flag, so the editor never sets it.
        if (item.answerType === 'ExerciseClip') return item;
        return { ...item, required: action.required };
      });
      return dirty(draft, items);
    }

    case 'addCustom': {
      const { question } = action;
      if (!answerTypeAllowed(draft.kind, question.answerType)) return draft;
      if (question.answerType === 'ExerciseClip') {
        if (!canAddClip(draft)) return draft;
        // A recording with no movement named is not answerable — `CustomQuestionRules` refuses it.
        if (question.exerciseId === null) return draft;
      }
      if (question.label.trim() === '') return draft;
      const item: CustomDraftItem = {
        kind: 'custom',
        id: action.id,
        label: question.label.trim(),
        answerType: question.answerType,
        options: CHOICE_ANSWER_TYPES.includes(question.answerType) ? question.options : [],
        required: question.answerType === 'ExerciseClip' ? false : question.required,
        exerciseId: question.answerType === 'ExerciseClip' ? question.exerciseId : null,
        sourceQuestionId: question.sourceQuestionId,
      };
      return dirty(draft, [...draft.items, item]);
    }

    /**
     * A patch to a question that is already on the form — today, the id the shelf minted for it.
     *
     * Three fields are NOT the patch's to set, and they are re-applied after the spread rather than
     * trusted to the caller: the identity, the answer type — which would be a way past the clip cap
     * `addCustom` holds — and the movement, which is chosen in the picker. A recording's „required"
     * is forced back to false for the reason the header gives: the server discards it.
     */
    case 'updateCustom': {
      const items = draft.items.map((item) => {
        if (item.kind !== 'custom' || item.id !== action.id) return item;
        const patched = { ...item, ...action.patch };
        return {
          ...patched,
          kind: 'custom' as const,
          id: item.id,
          answerType: item.answerType,
          exerciseId: item.exerciseId,
          required: item.answerType === 'ExerciseClip' ? false : patched.required,
        };
      });
      return dirty(draft, items);
    }

    case 'removeCustom':
      return dirty(
        draft,
        draft.items.filter((item) => !(item.kind === 'custom' && item.id === action.id)),
      );

    case 'move': {
      const customs = customItems(draft);
      const from = customs.findIndex((item) => item.id === action.id);
      if (from === -1) return draft;
      const to = Math.min(Math.max(action.targetIndex, 0), customs.length - 1);
      if (to === from) return draft;
      const reordered = [...customs];
      const [carried] = reordered.splice(from, 1);
      if (carried === undefined) return draft;
      reordered.splice(to, 0, carried);
      return dirty(draft, [...standardItems(draft), ...reordered]);
    }
  }
}

/**
 * A pointer drop, as the POSITION `move` takes.
 *
 * Two conversions, and each one has cost a defect somewhere in this app:
 *
 * · **An edge is a gap, not a row.** The bottom edge of row N is the gap AFTER it, N + 1 —
 *   `features/plan-day-programming` makes the same conversion on the same line of its own resolver.
 * · **A gap is not a position.** `move` removes the question before it re-inserts, so every gap
 *   BELOW the question's own place has shifted up by one by the time the insert happens.
 *
 * Skipping the first left half of every drop off by one: A B C D with D dropped on B's bottom edge
 * landed A D B C where the trainer aimed A B D C, and the live region read the wrong number out
 * loud.
 */
export function dropPosition(input: {
  /** Where the carried question sits now, among the custom questions. */
  readonly from: number;
  /** The row the pointer was released over. */
  readonly targetIndex: number;
  readonly edge: 'top' | 'bottom' | null;
}): number {
  const gap = input.edge === 'bottom' ? input.targetIndex + 1 : input.targetIndex;
  return gap > input.from ? gap - 1 : gap;
}

// ── What the intake refuses, as something the UI can render ──────────────────────────────────

export interface IntakeRestrictions {
  /** `PATCH /survey/template` carries no title, so the field is read-only with the reason beside it. */
  readonly titleLocked: boolean;
  /** Nor a cadence — the intake is filled once, at the client's first sign-in. */
  readonly cadenceLocked: boolean;
  /** R2: the four that are present in every intake and cannot be unticked. */
  readonly lockedCodes: readonly string[];
  /**
   * Who holds the form. `clients`: a check-in, assigned by hand (the bar and its picker). `everyone`:
   * the intake, which onboarding hands to every client (a sentence, nothing to manage). `nobody`: a
   * lead form, filled by people who are not clients yet — no bar at all (public-form R4).
   */
  readonly assignment: 'clients' | 'everyone' | 'nobody';
}

export function intakeRestrictions(draft: EditorDraft): IntakeRestrictions {
  const intake = draft.kind === 'intake';
  return {
    titleLocked: draft.kind !== 'checkIn',
    cadenceLocked: draft.kind !== 'checkIn',
    lockedCodes: intake ? standardItems(draft).filter((item) => item.locked).map((item) => item.code) : [],
    assignment: draft.kind === 'checkIn' ? 'clients' : intake ? 'everyone' : 'nobody',
  };
}

/** The sentence a refused control carries, so it explains itself while staying focusable. */
export function restrictionReason(kind: 'title' | 'cadence' | 'locked', t: TranslateKey): string {
  return t(`web:reports.editor.refused.${kind}`);
}

// ── The wire ─────────────────────────────────────────────────────────────────────────────────

function toItemInput(item: DraftItem): SurveyTemplateItemInput {
  if (item.kind === 'standard') {
    // A standard item is its CODE and its „required" and nothing else: the label, the answer type
    // and the options are the catalog's to say, and sending our copies of them would let a template
    // freeze a wording the catalog had since corrected.
    return { code: item.code, required: item.required };
  }
  return {
    id: item.id,
    label: item.label,
    answerType: item.answerType,
    options: item.options.length > 0 ? [...item.options] : null,
    required: item.required,
    exerciseId: item.exerciseId,
    sourceQuestionId: item.sourceQuestionId,
  };
}

/**
 * The whole ordered form, as the PATCH takes it. Always complete, never a delta — see the header.
 */
export function toTemplateItems(draft: EditorDraft): SurveyTemplateItemInput[] {
  return [
    ...standardItems(draft).filter((item) => item.included),
    ...customItems(draft),
  ].map(toItemInput);
}

/**
 * The form as a person filling it would be asked it — the questions `toTemplateItems` would save, in
 * the same order, in the shape the survey endpoints answer (`SurveyQuestionView`). What a preview
 * runs; nothing is sent from it.
 *
 * A catalog question takes its answer type, choices and mapping from the catalog, as the server's
 * resolution does; its key is its code, a trainer's own question's key its id.
 */
export function toQuestionViews(
  draft: EditorDraft,
  catalog: SurveyCatalogResponse | null | undefined,
): SurveyQuestionView[] {
  const byCode = new Map(catalogQuestions(catalog, draft.kind).map((question) => [question.code ?? '', question]));
  return [...standardItems(draft).filter((item) => item.included), ...customItems(draft)].map((item) => {
    if (item.kind === 'custom') {
      return {
        key: item.id,
        id: item.id,
        label: item.label,
        answerType: item.answerType,
        options: item.options.length > 0 ? [...item.options] : null,
        required: item.required,
        exerciseId: item.exerciseId,
      };
    }
    const question = byCode.get(item.code);
    return {
      key: item.code,
      code: item.code,
      label: item.label,
      answerType: item.answerType,
      options: question?.options ?? null,
      mapTarget: item.mapTarget,
      section: item.section,
      required: item.required,
      isHealthData: item.section === 'health',
    };
  });
}

/** `PATCH /survey/template/{id}`. The intake's own route takes items and refuses the rest. */
export function toPatchBody(draft: EditorDraft): UpdateSurveyTemplateRequest {
  // The intake's own route and the lead form's branch both take items and refuse the rest.
  if (draft.kind !== 'checkIn') return { items: toTemplateItems(draft) };
  return {
    title: draft.title.trim(),
    cadence: draft.cadence === null ? null : toCadenceInput(draft.cadence),
    items: toTemplateItems(draft),
  };
}

/** `POST /survey/template` — a check-in that does not exist yet, or a duplicate of one that does. */
export function toCreateBody(draft: EditorDraft): CreateSurveyTemplateRequest {
  return {
    title: draft.title.trim(),
    cadence: draft.cadence === null ? null : toCadenceInput(draft.cadence),
    items: toTemplateItems(draft),
  };
}

/**
 * The same form with every server-owned identity dropped — „Duplikuj ankietę".
 *
 * A copy carries the questions and the rhythm and nothing else: no template id, no assignments, and
 * no custom-question ids, because an id the server minted for one template is not this one's. The
 * `sourceQuestionId`s DO travel — the shelf entry they name is the trainer's own and the copy is as
 * much a use of it as the original.
 *
 * `mintId` arrives as a parameter for the reason `addCustom` takes its id: a fresh identity is a
 * dependency, and a module that reached for `crypto` could not be pinned by a test. The ids matter
 * before the save as well as after it — they are the React keys and what a reorder carries.
 */
export function duplicateDraft(
  draft: EditorDraft,
  title: string,
  mintId: () => string,
): EditorDraft {
  return {
    ...draft,
    templateId: null,
    title,
    active: true,
    items: draft.items.map((item) => (item.kind === 'custom' ? { ...item, id: mintId() } : item)),
    dirty: true,
  };
}

// ── Whether the save may act at all ──────────────────────────────────────────────────────────

/** Why „Zapisz zmiany" is refused, or `null` when it may act. */
export type SaveRefusal = 'clean' | 'title' | 'noQuestions' | 'cadence';

export function saveRefusal(draft: EditorDraft, options?: { readonly requireDirty?: boolean }): SaveRefusal | null {
  if (options?.requireDirty !== false && !draft.dirty) return 'clean';
  if (draft.kind === 'checkIn' && draft.title.trim() === '') return 'title';
  // `SurveyTemplateProcessor`: a check-in must hold at least one question, and a create refuses an
  // empty list whatever the kind.
  if (toTemplateItems(draft).length === 0) return 'noQuestions';
  if (cadenceProblems(draft.cadence).length > 0) return 'cadence';
  return null;
}

export function saveRefusalMessage(refusal: SaveRefusal, t: TranslateKey): string {
  return t(`web:reports.editor.save.refused.${refusal}`);
}
